import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Button, Modal } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TOKEN_SHOWN_ONCE } from './api-key-rules';

export interface ApiKeyTokenDialogProps {
  open: boolean;
  onClose: () => void;
  /** The plaintext, exactly as the create response returned it. */
  token: string;
  /** The key's own name, so the dialog says WHICH credential this is. */
  keyName: string;
}

/*
 * The one moment the plaintext token exists anywhere outside the caller that
 * will use it.
 *
 * 🔴 **THE TOKEN IS RETURNED EXACTLY ONCE, AT CREATION, AND CANNOT BE
 * RECOVERED.** Only a sha256 and a 12-character prefix are stored, so there is
 * no second read — not from this app, not from the database, not from support.
 * Closing this dialog without copying costs a new key and an update wherever
 * the old one was configured.
 *
 * ⛔ **WHERE THE TOKEN MUST NEVER GO, and every one of these is reachable by
 * an obvious-looking edit:**
 *
 *   - **no `console.*`**, at any level, not even behind a debug flag;
 *   - **no `title`** — a `title` is copied by accessibility tooling, surfaces in
 *     screenshots and outlives the dialog in a stale tooltip;
 *   - **no `localStorage`, `sessionStorage` or URL** — the whole point of the
 *     server never storing it is defeated by this app storing it;
 *   - **no error message.** The copy failure below deliberately says nothing
 *     about the value, because an error string is the one piece of text most
 *     likely to be pasted into a bug report or a chat.
 *
 * This repository has had live credentials exposed four times and the owner has
 * declined to rotate them. **Do not add a fifth route.**
 */
export function ApiKeyTokenDialog({
  open,
  onClose,
  token,
  keyName,
}: ApiKeyTokenDialogProps): ReactElement | null {
  /*
   * ⚠️ **NOTHING IS MOUNTED WHILE THIS IS CLOSED, AND HERE THAT IS A SECURITY
   * PROPERTY RATHER THAN THE USUAL TIDINESS.** The house pattern (see
   * `PermissionSetFormModal`) exists so a form's state is fresh on every open;
   * this dialog's "state" includes a live credential, so unmounting is also
   * what stops the plaintext sitting in a closed component's props between
   * opens. It removes the reset effect too — a fresh mount cannot open still
   * claiming the PREVIOUS token was copied.
   */
  if (!open) return null;
  return <ApiKeyToken onClose={onClose} token={token} keyName={keyName} />;
}

function ApiKeyToken({
  onClose,
  token,
  keyName,
}: {
  onClose: () => void;
  token: string;
  keyName: string;
}) {
  /*
   * Focus lands on COPY rather than on the dismiss button, which inverts this
   * app's usual confirmation shape on purpose: everywhere else the destructive
   * option is the one being confirmed, so focus starts on Cancel. Here the
   * destructive act IS dismissing — a stray Enter on "Done" destroys the value.
   */
  const copyRef = useRef<HTMLButtonElement>(null);
  const tokenRef = useRef<HTMLInputElement>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  // The acknowledgement is transient, so it cannot be mistaken for a permanent
  // "this is safely stored somewhere" state on a dialog about a value that is
  // stored nowhere.
  useEffect(() => {
    if (copyState !== 'copied') return;
    const timer = setTimeout(() => setCopyState('idle'), 4000);
    return () => clearTimeout(timer);
  }, [copyState]);

  const copy = useCallback(async () => {
    /*
     * `navigator.clipboard` is undefined outside a secure context and its write
     * can be refused by permissions policy, so both the absence and the
     * rejection are handled — and both resolve to the SAME state, because the
     * recovery is identical and a reader does not need to know which of the two
     * browser-side reasons applied.
     */
    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(token);
      setCopyState('copied');
    } catch {
      /*
       * ⛔ **NOTHING FROM THE CAUGHT ERROR IS RENDERED OR LOGGED.** The value
       * being copied is a live credential, and a copy failure is the moment
       * somebody is most likely to paste whatever the app said into a support
       * thread. The recovery is stated instead, and the box below is already
       * selectable for exactly this.
       */
      setCopyState('failed');
      tokenRef.current?.select();
    }
  }, [token]);

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Copy this token now"
      /*
       * A mis-click on the backdrop would destroy a value that cannot be
       * recovered. Escape still closes — `Modal` owns that and this dialog is
       * not the place to fight it — so the warning below is what has to do the
       * work, and it is above the fold.
       */
      dismissOnBackdrop={false}
      initialFocusRef={copyRef}
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-4">
        {/*
          * ⛔ **PROTECTED — a helper-text sweep must not take this.** It names
          * an irreversible consequence of an ordinary gesture (closing a
          * dialog), which is the documented exception to the no-helper-text
          * rule, and it is the only statement anywhere that this value has no
          * second read.
          */}
        <Notice tone="warning" role="alert">
          {TOKEN_SHOWN_ONCE}
        </Notice>

        <div className="min-w-0">
          <p className="m-0 text-xs font-medium text-fg-secondary">
            Token for <span className="[overflow-wrap:anywhere]">{keyName}</span>
          </p>
          {/*
            * A read-only INPUT rather than a `<code>` block, for two reasons
            * that both matter at 320px: it gives select-all for free, which is
            * the manual recovery when the clipboard is refused, and it scrolls
            * horizontally instead of wrapping a 40-odd character secret across
            * three lines where a transcription error is invisible.
            *
            * ⚠️ **Deliberately NOT `type="password"`.** The reader is here to
            * read it, and a masked box invites a browser password manager to
            * offer to save a credential this app has just promised not to keep.
            */}
          <input
            ref={tokenRef}
            readOnly
            value={token}
            aria-label={`API token for ${keyName}`}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            onFocus={(event) => event.target.select()}
            className="mt-1 block w-full min-w-0 rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-xs text-fg outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button ref={copyRef} variant="secondary" size="sm" onClick={() => void copy()}>
            Copy token
          </Button>
          {/*
            * `role="status"` rather than `alert` on the success arm: a
            * confirmation is not an interruption. The failure arm is the one
            * that changes what the reader has to do next, so it gets `alert`.
            */}
          {copyState === 'copied' && (
            <span role="status" className="text-xs text-success">
              Copied.
            </span>
          )}
          {copyState === 'failed' && (
            <span role="alert" className="min-w-0 text-xs text-danger">
              This browser would not let SEED EMERGE use the clipboard. The token is selected above —
              copy it by hand.
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}
